import { sum } from "lodash";
import { createSelector } from "reselect";

import { SaleLotType } from "constants/saleLots";
import { ScanLabelArchetype } from "constants/scanLabels";

import { getLivestockSaleId } from "lib/navigation";
import { getBestMatchingSplit } from "lib/splits";

import { selectVendorRelationshipsSummaryByBusinessIdLookup } from "../businessRelations";
import { selectOutOfSyncFieldNamesByBusinessId } from "../businesses";
import {
  selectResolvedSaleLotCommentCountBySaleLotIdLookup,
  selectUnresolvedSaleLotCommentCountBySaleLotIdLookup,
} from "../comments";
import { selectAgencyBySaleLotIdLookup } from "../consignmentsCalculated";
import { getCurrentSale } from "../currentSale";
import { selectPenScanLotIdsBySaleLotIdLookup } from "../globalSearch/penScanLotFilters";
import { selectFilteredSaleLotIds } from "../globalSearch/saleLotFilters";
import {
  selectEidsByPenScanLotIdLookup,
  selectEidsBySaleLotIdLookup,
} from "../indexes";
import {
  getAuctionPens,
  getBusinesses,
  getPenScanLots,
  getSaleSubTypes,
  getSpecies,
} from "../root";
import { getLabels } from "../root.state";
import {
  getSaleLotsBySale,
  selectNeverEverAccreditationStatusBySaleLotIdLookup,
  selectXeroBusinessesBySaleLotIdLookup,
} from "../saleLots/index";
import { selectEffectiveSaleTitleBySaleIdLookup } from "../sales";
import { selectDeploymentIdBySaleLotIdLookup } from "../saleLotsCalculated";
import { selectNotAngusVerifiedCountBySaleLotIdLookup } from "../scans";
import { selectScanLabelArchetypeCountsBySaleLotIdLookup } from "../scanLabel";
import { selectDeploymentMarkOrderLookup } from "../speciesAttributes";
import {
  selectBuyerSplitByKeyLookup,
  selectVendorSplitByKeyLookup,
} from "../splits";
import { resolveEffectiveVendorCommission } from "lib/vendorCommission";
import {
  selectSaleVendorCommissionByDeploymentIdLookup,
  selectVendorCommissionForCurrentSaleByDeploymentIdLookup,
  selectVendorCommissionForCurrentSaleByBusinessIdLookup,
} from "../vendorCommissions";

const selectEnhancedCurrentSale = createSelector(
  [getCurrentSale, getSaleSubTypes, getSpecies],
  (sale, saleSubTypes, species) =>
    Object.assign({}, sale, {
      species: species[sale.species_id],
      saleSubType: saleSubTypes[sale.sale_sub_type_id],
    }),
);

export const selectGeneralSaleLotsAggridData = createSelector(
  [
    getSaleLotsBySale,
    selectUnresolvedSaleLotCommentCountBySaleLotIdLookup,
    selectResolvedSaleLotCommentCountBySaleLotIdLookup,
    selectAgencyBySaleLotIdLookup,
    selectVendorRelationshipsSummaryByBusinessIdLookup,
    selectNotAngusVerifiedCountBySaleLotIdLookup,
    getLabels,
    selectFilteredSaleLotIds,
    getBusinesses,
    selectOutOfSyncFieldNamesByBusinessId,
    selectXeroBusinessesBySaleLotIdLookup,
    selectPenScanLotIdsBySaleLotIdLookup,
    getPenScanLots,
    getAuctionPens,
    selectEidsByPenScanLotIdLookup,
    selectEidsBySaleLotIdLookup,
    selectEnhancedCurrentSale,
    selectDeploymentMarkOrderLookup,
    selectNeverEverAccreditationStatusBySaleLotIdLookup,
    selectDeploymentIdBySaleLotIdLookup,
    selectVendorSplitByKeyLookup,
    selectBuyerSplitByKeyLookup,
    selectEffectiveSaleTitleBySaleIdLookup,
    selectVendorCommissionForCurrentSaleByDeploymentIdLookup,
    selectSaleVendorCommissionByDeploymentIdLookup,
    selectVendorCommissionForCurrentSaleByBusinessIdLookup,
    selectScanLabelArchetypeCountsBySaleLotIdLookup,
  ],
  (
    saleLots,
    unresolvedSaleLotCommentCountBySaleLotIdLookup,
    resolvedSaleLotCommentCountBySaleLotIdLookup,
    agencyBySaleLotIdLookup,
    vendorRelationshipsSummaryByBusinessIdLookup,
    notAngusVerifiedCountBySaleLotIdLookup,
    labelsLookup,
    filteredSaleLotIds,
    businesses,
    outOfSyncByBusinessIdLookup,
    xeroBusinessesBySaleLotIdLookup,
    penScanLotIdsBySaleLotId,
    penScanLotByIdLookup,
    auctionPens,
    eidsByPenScanLotIdLookup,
    eidsBySaleLotIdLookup,
    currentSale,
    deploymentMarkOrderLookup,
    neverEverAccreditationStatusBySaleLotIdLookup,
    deploymentIdBySaleLotIdLookup,
    vendorSplitByKeyLookup,
    buyerSplitByKeyLookup,
    effectiveSaleTitleBySaleIdLookup,
    vendorCommissionForCurrentSaleByDeploymentIdLookup,
    saleVendorCommissionByDeploymentIdLookup,
    vendorCommissionForCurrentSaleByBusinessIdLookup,
    scanLabelArchetypeCountsBySaleLotIdLookup,
  ) =>
    saleLots
      .filter(
        saleLot =>
          filteredSaleLotIds.includes(saleLot.id) &&
          saleLot.saleLotType !== SaleLotType.VENDOR_SPLIT,
      )
      .map(saleLot => {
        const { consignment, buyer = {}, vendor = {}, id: saleLotId } = saleLot;
        const xeroBusinesses = xeroBusinessesBySaleLotIdLookup[saleLot.id];
        const vendorId = vendor?.id;
        const vendorRelationshipsSummary =
          vendorRelationshipsSummaryByBusinessIdLookup[vendorId];
        const penScanLots = penScanLotIdsBySaleLotId[saleLot.id]?.map(
          penScanLotId => {
            const penScanLot = penScanLotByIdLookup[penScanLotId];
            const sellingPen = auctionPens[penScanLot?.sellingPenId];
            const scansCount =
              eidsByPenScanLotIdLookup[penScanLotId]?.length || 0;
            return Object.assign(
              {
                title: `${sellingPen?.start_pen} ${scansCount} / ${penScanLot?.quantity}`,
              },
              penScanLotByIdLookup?.[penScanLotId],
            );
          },
        );

        const saleLotPenScanLotIds = penScanLotIdsBySaleLotId[saleLotId] || [];

        const saleLotHasPenScanLots = !!saleLotPenScanLotIds?.length;

        const saleLotHasMoreThanOnePenScanLot = saleLotPenScanLotIds.length > 1;

        const saleLotEids = eidsBySaleLotIdLookup[saleLotId] || [];

        const saleLotEidsCount = saleLotEids.length;

        const saleLotPenScanLotEidsCount = sum(
          saleLotPenScanLotIds.map(
            penScanLotId => eidsByPenScanLotIdLookup[penScanLotId].length,
          ),
        );

        const saleLotQuantityDoesNotEqualSaleLotEidsCount =
          saleLot.quantity !== saleLotEidsCount;

        let rowWarning = null;

        // if the sale lot is associated to more than one pen scan lot
        // or the amount of sale lot eids is not equal to the the count
        // of pen scan lot eids

        if (saleLotHasPenScanLots) {
          if (saleLotHasMoreThanOnePenScanLot) {
            rowWarning = "Sale Lot has more than one Pen Scan Lot";
          } else if (saleLotEidsCount !== saleLotPenScanLotEidsCount) {
            rowWarning =
              "Sale Lot Scans count does not equal Pen Scan Lot Scans count";
          } else if (saleLotQuantityDoesNotEqualSaleLotEidsCount) {
            rowWarning =
              "Sale Lot Scans count does not equal Sale Lot hd count";
          }
        }

        const deploymentId = deploymentIdBySaleLotIdLookup[saleLotId];

        const marksWithOrder = saleLot.marks.map(mark => ({
          ...mark,
          order: deploymentMarkOrderLookup[deploymentId]?.[mark.location],
        }));

        const neverEverAccreditationStatus =
          neverEverAccreditationStatusBySaleLotIdLookup[saleLot.id];

        // vendor/buyer splits
        const livestockSaleId = getLivestockSaleId();
        const vendorSplit = getBestMatchingSplit({
          splitByKeyLookup: vendorSplitByKeyLookup,
          primaryBusinessId: vendorId,
          deploymentId,
          livestockSaleId,
          consignmentId: saleLot.consignment_id,
          saleLotId,
        });

        const buyerSplit = getBestMatchingSplit({
          splitByKeyLookup: buyerSplitByKeyLookup,
          primaryBusinessId: buyer.id,
          deploymentId,
          livestockSaleId,
          saleLotId,
        });

        const effectiveVendorCommission = resolveEffectiveVendorCommission({
          consignmentCommission: consignment?.vendorCommission,
          deploymentCommission:
            vendorCommissionForCurrentSaleByDeploymentIdLookup[deploymentId],
          saleCommission:
            saleVendorCommissionByDeploymentIdLookup[deploymentId],
          vendorCommission:
            vendorCommissionForCurrentSaleByBusinessIdLookup[vendorId],
        });

        const effectiveTitle =
          effectiveSaleTitleBySaleIdLookup[livestockSaleId];

        const scanLabelArchetypeCounts =
          scanLabelArchetypeCountsBySaleLotIdLookup[saleLot.id] || {};

        const nonReaderCount =
          scanLabelArchetypeCounts[ScanLabelArchetype.NON_READER] || 0;
        const noTagCount =
          scanLabelArchetypeCounts[ScanLabelArchetype.NO_TAG] || 0;
        const deadCount =
          scanLabelArchetypeCounts[ScanLabelArchetype.DECEASED] || 0;

        const tagsUsedCount =
          saleLot.quantityTagsUsed || noTagCount + nonReaderCount;

        return Object.assign({}, saleLot, {
          agency: agencyBySaleLotIdLookup[saleLot.id],
          invoiceToBusiness: businesses[saleLot.invoiceToBusinessId] || {},
          notAngusVerifiedCount:
            notAngusVerifiedCountBySaleLotIdLookup[saleLot.id],
          labels: saleLot.labels?.map(id => labelsLookup[id]?.name || "") || [],
          livestockSale: { ...currentSale, sale_title: effectiveTitle },
          marks: marksWithOrder,
          neverEverAccreditationStatus,
          vendorRelationships: vendorRelationshipsSummary?.text,
          vendorRelationshipsTooltip: vendorRelationshipsSummary?.tooltip,
          resolvedSaleLotCommentCount:
            resolvedSaleLotCommentCountBySaleLotIdLookup[saleLot.id],
          unresolvedSaleLotCommentCount:
            unresolvedSaleLotCommentCountBySaleLotIdLookup[saleLot.id],
          vendor,
          xeroBuyer: xeroBusinesses.buyer,
          xeroVendor: xeroBusinesses.vendor,
          vendorDataOutOfSync: outOfSyncByBusinessIdLookup[vendor?.id],
          buyerDataOutOfSync: outOfSyncByBusinessIdLookup[buyer?.id],
          penScanLots,
          rowWarning,
          transporter: businesses[saleLot.transporterId] || {},
          effectiveVendorCommission,
          vendorSplit,
          buyerSplit,
          tagsUsedCount,
          nonReaderCount,
          noTagCount,
          deadCount,
        });
      }),
);
